import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { promoteDemoTenant, type DemoTenantListItem } from '../../../api/demoTenants.api';
import { fetchActivePlans } from '../../../api/plans.api';
import type { AdminPlan } from '../../../types/plan.types';

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white';

interface PromoteDemoTenantModalProps {
  open: boolean;
  tenant: DemoTenantListItem | null;
  onClose: () => void;
  onPromoted: () => void;
}

export default function PromoteDemoTenantModal({
  open,
  tenant,
  onClose,
  onPromoted,
}: PromoteDemoTenantModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [planId, setPlanId] = useState<number | ''>('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError('');
    setPlanId('');
    setPaymentReference('');
    setPaymentNotes('');
    setLoadingPlans(true);
    void (async () => {
      const r = await fetchActivePlans();
      if (cancelled) return;
      setLoadingPlans(false);
      if (r.ok === false) {
        if (r.unauthorized) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(r.message);
        setPlans([]);
        return;
      }
      setPlans(r.plans);
      if (r.plans[0]) setPlanId(r.plans[0].id);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, navigate]);

  if (!open || !tenant) return null;

  const selectedPlan = plans.find((p) => p.id === planId) ?? null;

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!planId) {
      setError(t('demo_tenants.promote.plan_required'));
      return;
    }
    setError('');
    setLoading(true);
    const r = await promoteDemoTenant(tenant.id, {
      plan_id: Number(planId),
      mark_as_paid: true,
      payment_method: 'manual',
      payment_reference: paymentReference.trim() || undefined,
      payment_notes: paymentNotes.trim() || undefined,
    });
    setLoading(false);
    if (r.ok === false) {
      if (r.unauthorized) {
        navigate('/admin/login', { replace: true });
        return;
      }
      setError(r.message);
      return;
    }
    onPromoted();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-bold text-gray-900">{t('demo_tenants.promote.title')}</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-4">
          <div className="rounded-xl bg-teal-50 border border-teal-100 px-3 py-2.5 text-xs text-teal-900 leading-relaxed">
            {t('demo_tenants.promote.hint')}
          </div>

          <div className="rounded-xl bg-gray-50 px-3 py-2.5">
            <p className="text-sm font-semibold text-gray-900">{tenant.name}</p>
            <p className="text-xs font-mono text-gray-500 mt-0.5">{tenant.slug}</p>
            <p className="text-xs text-gray-500 mt-1 break-all">
              {tenant.admin_email || tenant.email || '—'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('demo_tenants.promote.fields.plan')}
            </label>
            {loadingPlans ? (
              <p className="text-sm text-gray-500 py-2">{t('demo_tenants.promote.loading_plans')}</p>
            ) : (
              <select
                required
                value={planId === '' ? '' : String(planId)}
                onChange={(e) => setPlanId(e.target.value ? Number(e.target.value) : '')}
                disabled={loading || plans.length === 0}
                className={inputCls}
              >
                <option value="">{t('demo_tenants.promote.fields.plan_placeholder')}</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title || plan.name}
                    {plan.price ? ` — ${plan.price}` : ''}
                    {plan.days || plan.duration_days
                      ? ` (${plan.days || plan.duration_days} ${t('demo_tenants.promote.days_unit')})`
                      : ''}
                  </option>
                ))}
              </select>
            )}
            {selectedPlan ? (
              <p className="text-xs text-gray-500 mt-2">
                {t('demo_tenants.promote.selected_help', {
                  plan: selectedPlan.title || selectedPlan.name,
                  days: selectedPlan.days || selectedPlan.duration_days || '—',
                })}
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('demo_tenants.promote.fields.payment_reference')}
            </label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              disabled={loading}
              className={inputCls}
              placeholder={t('demo_tenants.promote.fields.payment_reference_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('demo_tenants.promote.fields.payment_notes')}
            </label>
            <textarea
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              disabled={loading}
              rows={3}
              className={inputCls}
              placeholder={t('demo_tenants.promote.fields.payment_notes_placeholder')}
            />
          </div>

          {error ? (
            <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{error}</p>
          ) : null}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              {t('demo_tenants.promote.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || loadingPlans || !planId}
              className="flex-1 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 cursor-pointer disabled:opacity-50"
            >
              {loading ? t('demo_tenants.promote.submitting') : t('demo_tenants.promote.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
